import { useCallback, useState } from "react";
import type { TooltipState, TooltipDatum } from "../types/atlasTooltip";

export interface UseTooltipStateReturn<T = TooltipDatum> {
  tooltipState: TooltipState<T> | undefined;
  showTooltip: (data: T, coords?: [number, number], fillColor?: string) => void;
  hideTooltip: () => void;
  fixTooltip: (data: T, coords: [number, number], fillColor?: string) => void;
  clearFixed: () => void;
}

/**
 * Generic tooltip state manager for visualization charts.
 *
 * Manages hover/fixed tooltip state in a consistent shape across all chart types.
 * Does NOT handle rendering — pass the returned state to the portal Tooltip component.
 *
 * Performance: wrap with `TooltipStateIsolationProvider`, render the portal via
 * `TooltipStateBridge` (from `@atlas-int/viz/shared`), and keep the chart in
 * `memo(...)` using `useTooltipDispatchRef` for hover handlers so Apollo/URL/legend
 * do not rerender on hover. Connected geomap, treemap, and market share follow this;
 * overtime uses a custom `useOvertimeTooltip` stack instead.
 *
 * @param onHighlight Optional callback invoked when tooltip is fixed (with item id) or cleared (undefined).
 */
export const useTooltipState = <T = TooltipDatum>(
  onHighlight?: (item: string | undefined) => void,
  dataVersion?: unknown,
): UseTooltipStateReturn<T> => {
  const [tooltipState, setTooltipState] = useState<TooltipState<T> | undefined>(undefined);

  // Synchronous reset during render when data context changes.
  // React-recommended "adjust state during render" pattern — no useEffect needed.
  const [prevDataVersion, setPrevDataVersion] = useState(dataVersion);
  if (dataVersion !== undefined && dataVersion !== prevDataVersion) {
    setPrevDataVersion(dataVersion);
    if (tooltipState !== undefined) {
      setTooltipState(undefined);
    }
    onHighlight?.(undefined);
  }

  const showTooltip = useCallback(
    (data: T, _coords?: [number, number], fillColor?: string) => {
      // Hover position is driven by the portal Tooltip's onMouseMoveCapture
      // (see src/visualization/components/Tooltip.tsx). Do not store per-frame
      // coordinates in React state or every mousemove rerenders the chart tree.
      setTooltipState({
        isFixed: false,
        contentData: data,
        fillColor,
      });
    },
    [],
  );

  const hideTooltip = useCallback(() => {
    setTooltipState(undefined);
    onHighlight?.(undefined);
  }, [onHighlight]);

  const fixTooltip = useCallback(
    (data: T, coords: [number, number], fillColor?: string) => {
      setTooltipState({
        isFixed: true,
        isFixedCoords: coords,
        contentData: data,
        fillColor,
      });
    },
    [],
  );

  const clearFixed = useCallback(() => {
    setTooltipState(undefined);
    onHighlight?.(undefined);
  }, [onHighlight]);

  return { tooltipState, showTooltip, hideTooltip, fixTooltip, clearFixed };
};
